/**
 * <sale-discount-price>
 *
 * Renders the discounted price next to the struck-through original during a sitewide sale.
 *
 * The theme's own <sale-price> and <compare-at-price> elements re-render themselves from the variant JSON on
 * `variant:change` (see theme.js), which would otherwise wipe anything Liquid had baked in the moment a shopper
 * picked a different size. This element follows the same contract so the sale price tracks the selection.
 *
 * Prices come from a `data-prices` map rendered by Liquid rather than being formatted here, so the sale price uses
 * the shop's exact money format and rounds identically to the value the discount applies at checkout.
 */
class SaleDiscountPrice extends HTMLElement {
    constructor() {
        super();
        this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
        try {
            this._prices = JSON.parse(this.dataset.prices || "null");
        } catch (error) {
            this._prices = null;
        }

        document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
        document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
        const variant = event.detail.variant;

        if (!variant || !this._prices) {
            return;
        }

        const price = this._prices[String(variant["id"])];

        if (price) {
            // Mirrors <sale-price>: replace the trailing price node and leave the screen-reader label alone.
            this.lastChild.replaceWith(document.createTextNode(price));
        }
    }
}

if (!window.customElements.get("sale-discount-price")) {
    window.customElements.define("sale-discount-price", SaleDiscountPrice);
}

export { SaleDiscountPrice };
